import { Form } from "antd";
import { findIndex, isEmpty, map, split, uniqBy } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, parseSelectOptions } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";
import { getOptions } from "../QualysInfra/QualysInfraMappingForm";

export const QUALYS_CONTAINER_SECURITY_TYPES = [
  {
    label: "Container",
    value: "containers",
  },
  {
    label: "Image",
    value: "images",
  },
];

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  type: "containers",
};

const QualysContainerSecurityMappingForm = forwardRef(
  function QualysContainerSecurityMappingForm(props, ref) {
    const [form] = Form.useForm();
    const typeRef = useRef({});
    const functionRef = useRef({});
    const selectedNames = useRef({});

    const [initialValues] = useState(() => {
      if (props.record) {
        return [];
      }
      return [{ ...initialFormValues }];
    });

    const fetchProjects = useCallback(
      (params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            ...params,
          });
        return { promise, cancel };
      },
      [props.toolName, props.loginConfigId],
    );

    const onIntegrationTypeChange = useCallback(
      (value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        let updatedConfiguration = [...configurations];

        updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          integration: value,
          projectId: undefined,
        };

        form.setFieldsValue({ configurations: updatedConfiguration });
      },
      [form],
    );

    const getExtraFieldsData = useCallback((config, index) => {
      const { projectId, type } = config;
      const extraFieldData = {
        imageId: "",
        imageName: "",
        containerId: "",
        containerName: "",
        type,
      };
      //handling multiple mapping case using index
      //to prevent wrong name map when multiple configurations are saved
      const configSelectedNames = selectedNames.current[index];
      switch (type) {
        case "images":
          extraFieldData.imageId = !projectId ? "" : projectId.join();
          extraFieldData.imageName = !projectId
            ? ""
            : configSelectedNames.join();
          break;
        case "containers":
        default:
          extraFieldData.containerId = !projectId ? "" : projectId.join();
          extraFieldData.containerName = !projectId
            ? ""
            : configSelectedNames.join();
          break;
      }
      return extraFieldData;
    }, []);

    const getParsedOptions = useCallback(
      (type, options) => {
        if (props.record) {
          const { imageId, imageName, containerId, containerName } =
            props.record;
          const recordOptions = !isEmpty(imageId)
            ? getOptions(imageId, imageName)
            : !isEmpty(containerId)
              ? getOptions(containerId, containerName)
              : [];
          const finaloptions = parseSelectOptions(
            uniqBy([...options, ...recordOptions]),
          );
          return parseOptionsWithCustomizedLabel(type, finaloptions);
        }
        return parseOptionsWithCustomizedLabel(type, options);
      },
      [props.record],
    );

    const fields = useMemo(
      () => [
        {
          name: "type",
          colSpan: 15,
          label: "Select Integration Type",
          type: FieldTypes.SELECT,
          options: QUALYS_CONTAINER_SECURITY_TYPES,
          props: {
            onChange: onIntegrationTypeChange,
          },
          required: true
        },
        {
          name: "projectId",
          colSpan: 15,
          label: "Projects",
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          props: {
            onChange: (value, field, selectedOptions) => {
              //handling multiple mapping case using index
              selectedNames.current[field.name] = map(selectedOptions, "name");
            },
            setExtraProps: (field, listField) => {
              const allConfigs = form.getFieldsValue().configurations;
              const type = allConfigs[listField.name]?.type;
              if (
                !typeRef.current[type] ||
                typeRef.current[type]?.type !== type
              ) {
                typeRef.current[type] = { type };
                // Cache a parseSelectOptions function for each integration type
                // so it isn’t re-created on every render, preventing redundant API calls
                functionRef.current[type] = (options) =>
                  getParsedOptions(type, options);
              }
              return {
                api: type && fetchProjects,
                apiParams: typeRef.current[type],
                resetOptionsProp: type,
                mode: "multiple",
                allowClear: true,
                parseSelectOptions: functionRef.current[type],
                optionFilterProp: "name",
              };
            },
          },
        },
      ],
      [fetchProjects, form, onIntegrationTypeChange, getParsedOptions],
    );

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { imageId, imageName, containerId, containerName } = config;
      const refIdArr = [containerId, imageId];
      const refNameArr = [containerName, imageName];
      const validIdIndex = findIndex(refIdArr, (value) => !isEmpty(value));
      //keeping the index as 0 as we show only one configuration on edit
      //as the configurations gets saved individually for multiple mappings too
      selectedNames.current[0] = containerName
        ? split(containerName, ",")
        : split(imageName, ",");

      return {
        projectId: refIdArr[validIdIndex]?.split(","),
        projectName: refNameArr[validIdIndex]?.split(","),
        integration: QUALYS_CONTAINER_SECURITY_TYPES[validIdIndex]?.value,
      };
    }, []);

    return (
      <GenericMappingForm
        ref={ref}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={fields}
        formDefaultValues={initialValues}
        form={form}
        {...props}
      />
    );
  },
);

export default QualysContainerSecurityMappingForm;

const parseOptionsWithCustomizedLabel = (type, options) => {
  const displayTypeID = type === "containers" ? "Container Id" : "Image Id";
  return map(options, (option) => ({
    value: option.id,
    label: (
      <span>
        {option.name} ({displayTypeID}:{" "}
        {option.otherProperties?.[
          type === "containers" ? "containerId" : "imageId"
        ] ?? option.id.substring(0, 12)}
        )
      </span>
    ),
    name: option.name,
  }));
};
